import React, { useEffect, useState } from 'react';
import './LandingAdmin.css';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import bookmeLandingConfig from '../../config/bookmeLanding.config';

const randomId = (prefix = 'item') =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const emptyFeature = () => ({
  id: randomId('feature'),
  title: '',
  desc: '',
  mediaType: 'image',
  media: '',
});

const emptyTestimonial = () => ({
  id: randomId('testimonial'),
  name: '',
  text: '',
});

const normalizeSettings = (data = {}) => ({
  ...bookmeLandingConfig,
  ...data,
  branding: {
    ...(bookmeLandingConfig.branding || {}),
    ...(data.branding || {}),
  },
  hero: {
    ...(bookmeLandingConfig.hero || {}),
    ...(data.hero || {}),
  },
  sections: {
    ...(bookmeLandingConfig.sections || {}),
    ...(data.sections || {}),
  },
  theme: {
    ...(bookmeLandingConfig.theme || {}),
    ...(data.theme || {}),
  },
  whatsapp: {
    ...(bookmeLandingConfig.whatsapp || {}),
    ...(data.whatsapp || {}),
  },
  features: Array.isArray(data.features) ? data.features : bookmeLandingConfig.features || [],
  testimonials: Array.isArray(data.testimonials) ? data.testimonials : bookmeLandingConfig.testimonials || [],
  gallery: Array.isArray(data.gallery) ? data.gallery : bookmeLandingConfig.gallery || [],
});

const BookMeLandingAdmin = () => {
  const [form, setForm] = useState(() => normalizeSettings(bookmeLandingConfig));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [uploadingHint, setUploadingHint] = useState('');
  const [activeTab, setActiveTab] = useState('branding');

  const tabs = [
    { id: 'branding', label: 'Branding' },
    { id: 'hero', label: 'Hero' },
    { id: 'texts', label: 'Textos' },
    { id: 'theme', label: 'Tema' },
    { id: 'features', label: 'Funcionalidades' },
    { id: 'testimonials', label: 'Testimonios' },
    { id: 'gallery', label: 'Galería' },
    { id: 'whatsapp', label: 'WhatsApp' },
  ];

  useEffect(() => {
    let cancelled = false;
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'settings', 'landing');
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
          await setDoc(docRef, bookmeLandingConfig);
          if (!cancelled) {
            setForm(normalizeSettings(bookmeLandingConfig));
          }
        } else if (!cancelled) {
          setForm(normalizeSettings(snap.data() || {}));
        }
      } catch (err) {
        console.error('❌ Error cargando BookMe landing settings:', err);
        if (!cancelled) {
          setForm(normalizeSettings(bookmeLandingConfig));
          setStatus('❌ No se pudo cargar la configuración. Se usan valores locales.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const uploadAsset = async (file, pathPrefix = 'bookme/landing') => {
    const safePath = `${pathPrefix}/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, safePath);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type || 'application/octet-stream',
    });
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        null,
        (error) => reject(error),
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const updateBranding = (field, value) => {
    setForm((prev) => ({
      ...prev,
      branding: { ...(prev.branding || {}), [field]: value },
    }));
  };

  const updateHero = (field, value) => {
    setForm((prev) => ({
      ...prev,
      hero: { ...(prev.hero || {}), [field]: value },
    }));
  };

  const updateSection = (field, value) => {
    setForm((prev) => ({
      ...prev,
      sections: { ...(prev.sections || {}), [field]: value },
    }));
  };

  const updateTheme = (field, value) => {
    setForm((prev) => ({
      ...prev,
      theme: { ...(prev.theme || {}), [field]: value },
    }));
  };

  const updateWhatsapp = (field, value) => {
    setForm((prev) => ({
      ...prev,
      whatsapp: { ...(prev.whatsapp || {}), [field]: value },
    }));
  };

  const updateFeature = (index, field, value) => {
    setForm((prev) => {
      const features = [...(prev.features || [])];
      features[index] = { ...features[index], [field]: value };
      return { ...prev, features };
    });
  };

  const addFeature = () => {
    setForm((prev) => ({
      ...prev,
      features: [...(prev.features || []), emptyFeature()],
    }));
  };

  const removeFeature = (index) => {
    setForm((prev) => ({
      ...prev,
      features: (prev.features || []).filter((_, idx) => idx !== index),
    }));
  };

  const updateTestimonial = (index, field, value) => {
    setForm((prev) => {
      const testimonials = [...(prev.testimonials || [])];
      testimonials[index] = { ...testimonials[index], [field]: value };
      return { ...prev, testimonials };
    });
  };

  const addTestimonial = () => {
    setForm((prev) => ({
      ...prev,
      testimonials: [...(prev.testimonials || []), emptyTestimonial()],
    }));
  };

  const removeTestimonial = (index) => {
    setForm((prev) => ({
      ...prev,
      testimonials: (prev.testimonials || []).filter((_, idx) => idx !== index),
    }));
  };

  const updateGalleryItem = (index, value) => {
    setForm((prev) => {
      const gallery = [...(prev.gallery || [])];
      gallery[index] = value;
      return { ...prev, gallery };
    });
  };

  const addGalleryItem = () => {
    setForm((prev) => ({
      ...prev,
      gallery: [...(prev.gallery || []), ''],
    }));
  };

  const removeGalleryItem = (index) => {
    setForm((prev) => ({
      ...prev,
      gallery: (prev.gallery || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingHint('Subiendo logo...');
    try {
      const url = await uploadAsset(file, 'bookme/landing/logo');
      updateBranding('logoUrl', url);
    } catch (err) {
      console.error('❌ Error subiendo logo BookMe', err);
      setStatus('❌ No se pudo subir el logo.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleHeroBackgroundUpload = async (file) => {
    if (!file) return;
    setUploadingHint('Subiendo fondo del hero...');
    try {
      const url = await uploadAsset(file, 'bookme/landing/hero');
      updateHero('backgroundImage', url);
    } catch (err) {
      console.error('❌ Error subiendo fondo del hero', err);
      setStatus('❌ No se pudo subir la imagen del hero.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleFeatureMediaUpload = async (index, file) => {
    if (!file) return;
    setUploadingHint('Subiendo media del feature...');
    try {
      const url = await uploadAsset(file, `bookme/landing/features/${form.features?.[index]?.id || 'feature'}`);
      updateFeature(index, 'media', url);
    } catch (err) {
      console.error('❌ Error subiendo media del feature', err);
      setStatus('❌ No se pudo subir la media del feature.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleGalleryUpload = async (files) => {
    if (!files || !files.length) return;
    setUploadingHint('Subiendo galería...');
    try {
      const uploaded = [];
      for (const file of files) {
        const url = await uploadAsset(file, 'bookme/landing/gallery');
        uploaded.push(url);
      }
      setForm((prev) => ({
        ...prev,
        gallery: [...(prev.gallery || []), ...uploaded],
      }));
    } catch (err) {
      console.error('❌ Error subiendo galería BookMe', err);
      setStatus('❌ No se pudieron subir imágenes.');
    } finally {
      setUploadingHint('');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus('');
    try {
      const payload = {
        branding: {
          name: form.branding?.name || '',
          logoUrl: form.branding?.logoUrl || '',
          logoAlt: form.branding?.logoAlt || '',
        },
        hero: {
          title: form.hero?.title || '',
          highlight: form.hero?.highlight || '',
          description: form.hero?.description || '',
          ctaText: form.hero?.ctaText || '',
          ctaLink: form.hero?.ctaLink || '',
          backgroundImage: form.hero?.backgroundImage || '',
        },
        sections: {
          ...(form.sections || {}),
        },
        theme: {
          ...(form.theme || {}),
        },
        whatsapp: {
          ...(form.whatsapp || {}),
        },
        features: (form.features || []).map((feature, idx) => ({
          id: feature.id || randomId(`feature-${idx}`),
          title: feature.title || '',
          desc: feature.desc || '',
          mediaType: feature.mediaType || 'image',
          media: feature.media || '',
        })),
        testimonials: (form.testimonials || []).map((t, idx) => ({
          id: t.id || randomId(`testimonial-${idx}`),
          name: t.name || '',
          text: t.text || '',
        })),
        gallery: (form.gallery || []).filter(Boolean),
      };
      const docRef = doc(db, 'settings', 'landing');
      await setDoc(docRef, payload, { merge: true });
      setStatus('✅ BookMe landing actualizada.');
    } catch (err) {
      console.error('❌ Error guardando BookMe landing', err);
      setStatus('❌ No se pudo guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="landing-admin">
      <h2>Editor de Landing BookMe</h2>
      <p className="text-sm text-gray-500 mb-4">
        Configura textos, imágenes y tema visual del landing BookMe.
      </p>
      {loading && <p>Cargando configuración...</p>}
      {uploadingHint && <p>{uploadingHint}</p>}
      <div className="tab-switcher">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? 'active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'branding' && (
      <section className="card">
        <h3>Branding</h3>
        <label>Nombre de marca
          <input type="text" value={form.branding?.name || ''} onChange={(e) => updateBranding('name', e.target.value)} />
        </label>
        <label>Logo (URL)
          <input type="text" value={form.branding?.logoUrl || ''} onChange={(e) => updateBranding('logoUrl', e.target.value)} placeholder="https://..." />
        </label>
        <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e.target.files?.[0])} />
        {form.branding?.logoUrl && <img src={form.branding.logoUrl} alt={form.branding?.logoAlt || 'Logo'} className="preview-image" />}
        <label>Texto alterno del logo
          <input type="text" value={form.branding?.logoAlt || ''} onChange={(e) => updateBranding('logoAlt', e.target.value)} />
        </label>
      </section>
      )}

      {activeTab === 'hero' && (
      <section className="card">
        <h3>Hero</h3>
        <label>Título principal
          <input type="text" value={form.hero?.title || ''} onChange={(e) => updateHero('title', e.target.value)} />
        </label>
        <label>Texto destacado
          <input type="text" value={form.hero?.highlight || ''} onChange={(e) => updateHero('highlight', e.target.value)} />
        </label>
        <label>Descripción
          <textarea value={form.hero?.description || ''} onChange={(e) => updateHero('description', e.target.value)} />
        </label>
        <div className="row two-columns">
          <label>Texto CTA
            <input type="text" value={form.hero?.ctaText || ''} onChange={(e) => updateHero('ctaText', e.target.value)} />
          </label>
          <label>Link CTA
            <input type="text" value={form.hero?.ctaLink || ''} onChange={(e) => updateHero('ctaLink', e.target.value)} />
          </label>
        </div>
        <label>Imagen de fondo (URL)
          <input
            type="text"
            value={form.hero?.backgroundImage || ''}
            onChange={(e) => updateHero('backgroundImage', e.target.value)}
            placeholder="https://..."
          />
        </label>
        <input type="file" accept="image/*" onChange={(e) => handleHeroBackgroundUpload(e.target.files?.[0])} />
        {form.hero?.backgroundImage && <img src={form.hero.backgroundImage} alt="Hero" className="preview-image" />}
      </section>
      )}

      {activeTab === 'texts' && (
      <section className="card">
        <h3>Textos por sección</h3>
        <label>Título de funcionalidades
          <input type="text" value={form.sections?.featuresTitle || ''} onChange={(e) => updateSection('featuresTitle', e.target.value)} />
        </label>
        <label>Subtítulo de funcionalidades
          <input type="text" value={form.sections?.featuresSubtitle || ''} onChange={(e) => updateSection('featuresSubtitle', e.target.value)} />
        </label>
        <label>Título de testimonios
          <input type="text" value={form.sections?.testimonialsTitle || ''} onChange={(e) => updateSection('testimonialsTitle', e.target.value)} />
        </label>
        <label>Título de galería
          <input type="text" value={form.sections?.galleryTitle || ''} onChange={(e) => updateSection('galleryTitle', e.target.value)} />
        </label>
        <label>Subtítulo de galería
          <input type="text" value={form.sections?.gallerySubtitle || ''} onChange={(e) => updateSection('gallerySubtitle', e.target.value)} />
        </label>
        <label>Título de formulario
          <input type="text" value={form.sections?.formTitle || ''} onChange={(e) => updateSection('formTitle', e.target.value)} />
        </label>
        <label>Subtítulo de formulario
          <textarea value={form.sections?.formSubtitle || ''} onChange={(e) => updateSection('formSubtitle', e.target.value)} />
        </label>
        <label>Nota del formulario
          <input type="text" value={form.sections?.formFootnote || ''} onChange={(e) => updateSection('formFootnote', e.target.value)} />
        </label>
        <label>Texto del footer
          <input type="text" value={form.sections?.footerText || ''} onChange={(e) => updateSection('footerText', e.target.value)} />
        </label>
        <div className="row two-columns">
          <label>Footer - texto enlace
            <input type="text" value={form.sections?.footerLinkLabel || ''} onChange={(e) => updateSection('footerLinkLabel', e.target.value)} />
          </label>
          <label>Footer - URL
            <input type="text" value={form.sections?.footerLinkUrl || ''} onChange={(e) => updateSection('footerLinkUrl', e.target.value)} />
          </label>
        </div>
      </section>
      )}

      {activeTab === 'theme' && (
      <section className="card">
        <h3>Paleta y tipografía</h3>
        <div className="row two-columns">
          <label>Primary
            <input type="text" value={form.theme?.primary || ''} onChange={(e) => updateTheme('primary', e.target.value)} placeholder="#f97316" />
          </label>
          <label>Primary Dark
            <input type="text" value={form.theme?.primaryDark || ''} onChange={(e) => updateTheme('primaryDark', e.target.value)} placeholder="#ea580c" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Accent
            <input type="text" value={form.theme?.accent || ''} onChange={(e) => updateTheme('accent', e.target.value)} placeholder="#f97316" />
          </label>
          <label>Texto principal
            <input type="text" value={form.theme?.text || ''} onChange={(e) => updateTheme('text', e.target.value)} placeholder="#111827" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Texto secundario
            <input type="text" value={form.theme?.muted || ''} onChange={(e) => updateTheme('muted', e.target.value)} placeholder="#6b7280" />
          </label>
          <label>Bordes
            <input type="text" value={form.theme?.border || ''} onChange={(e) => updateTheme('border', e.target.value)} placeholder="#e5e7eb" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Background
            <input type="text" value={form.theme?.background || ''} onChange={(e) => updateTheme('background', e.target.value)} placeholder="#ffffff" />
          </label>
          <label>Surface
            <input type="text" value={form.theme?.surface || ''} onChange={(e) => updateTheme('surface', e.target.value)} placeholder="#f9fafb" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Surface alterna
            <input type="text" value={form.theme?.surfaceAlt || ''} onChange={(e) => updateTheme('surfaceAlt', e.target.value)} placeholder="#f3f4f6" />
          </label>
          <label>Fondo de tarjetas
            <input type="text" value={form.theme?.cardBg || ''} onChange={(e) => updateTheme('cardBg', e.target.value)} placeholder="#ffffff" />
          </label>
        </div>
        <div className="row two-columns">
          <label>Hero gradient From
            <input type="text" value={form.theme?.heroFrom || ''} onChange={(e) => updateTheme('heroFrom', e.target.value)} placeholder="#fff7ed" />
          </label>
          <label>Hero gradient To
            <input type="text" value={form.theme?.heroTo || ''} onChange={(e) => updateTheme('heroTo', e.target.value)} placeholder="#ffffff" />
          </label>
        </div>
        <div className="row two-columns">
          <label>WhatsApp
            <input type="text" value={form.theme?.whatsapp || ''} onChange={(e) => updateTheme('whatsapp', e.target.value)} placeholder="#22c55e" />
          </label>
          <label>WhatsApp hover
            <input type="text" value={form.theme?.whatsappHover || ''} onChange={(e) => updateTheme('whatsappHover', e.target.value)} placeholder="#16a34a" />
          </label>
        </div>
        <label>Font family
          <input type="text" value={form.theme?.fontFamily || ''} onChange={(e) => updateTheme('fontFamily', e.target.value)} placeholder="e.g. 'Manrope', sans-serif" />
        </label>
        <label>Font URL (opcional)
          <input type="text" value={form.theme?.fontUrl || ''} onChange={(e) => updateTheme('fontUrl', e.target.value)} placeholder="https://fonts.googleapis.com/..." />
        </label>
      </section>
      )}

      {activeTab === 'features' && (
      <section className="card">
        <div className="card-header">
          <h3>Funcionalidades</h3>
          <button type="button" onClick={addFeature}>+ Agregar</button>
        </div>
        {(form.features || []).map((feature, index) => (
          <div key={feature.id || index} className="feature-block">
            <div className="row two-columns">
              <label>Título
                <input type="text" value={feature.title || ''} onChange={(e) => updateFeature(index, 'title', e.target.value)} />
              </label>
              <label>Tipo de media
                <select value={feature.mediaType || 'image'} onChange={(e) => updateFeature(index, 'mediaType', e.target.value)}>
                  <option value="image">Imagen</option>
                  <option value="video">Video</option>
                </select>
              </label>
            </div>
            <label>Descripción
              <textarea value={feature.desc || ''} onChange={(e) => updateFeature(index, 'desc', e.target.value)} />
            </label>
            <label>Media URL
              <input type="text" value={feature.media || ''} onChange={(e) => updateFeature(index, 'media', e.target.value)} placeholder="https://..." />
            </label>
            <input type="file" accept="image/*,video/*" onChange={(e) => handleFeatureMediaUpload(index, e.target.files?.[0])} />
            {feature.media && feature.mediaType !== 'video' && (
              <img src={feature.media} alt={feature.title || 'Feature'} className="preview-image" />
            )}
            {feature.media && feature.mediaType === 'video' && (
              <video src={feature.media} controls className="preview-image" />
            )}
            <button type="button" className="btn-delete" onClick={() => removeFeature(index)}>
              Eliminar funcionalidad
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'testimonials' && (
      <section className="card">
        <div className="card-header">
          <h3>Testimonios</h3>
          <button type="button" onClick={addTestimonial}>+ Agregar</button>
        </div>
        {(form.testimonials || []).map((t, index) => (
          <div key={t.id || index} className="testimonial-block">
            <label>Nombre
              <input type="text" value={t.name || ''} onChange={(e) => updateTestimonial(index, 'name', e.target.value)} />
            </label>
            <label>Texto
              <textarea value={t.text || ''} onChange={(e) => updateTestimonial(index, 'text', e.target.value)} />
            </label>
            <button type="button" className="btn-delete" onClick={() => removeTestimonial(index)}>
              Eliminar testimonio
            </button>
          </div>
        ))}
      </section>
      )}

      {activeTab === 'gallery' && (
      <section className="card">
        <div className="card-header">
          <h3>Galería</h3>
          <button type="button" onClick={addGalleryItem}>+ Agregar imagen</button>
        </div>
        {(form.gallery || []).map((item, index) => (
          <div key={`${item}-${index}`} className="feature-block">
            <label>Imagen URL
              <input type="text" value={item || ''} onChange={(e) => updateGalleryItem(index, e.target.value)} placeholder="https://..." />
            </label>
            {item && <img src={item} alt={`gallery-${index}`} className="preview-image" />}
            <button type="button" className="btn-delete" onClick={() => removeGalleryItem(index)}>
              Eliminar imagen
            </button>
          </div>
        ))}
        <label className="upload-label">
          Subir imágenes
          <input type="file" accept="image/*" multiple onChange={(e) => handleGalleryUpload(e.target.files)} />
        </label>
      </section>
      )}

      {activeTab === 'whatsapp' && (
      <section className="card">
        <h3>WhatsApp</h3>
        <div className="row two-columns">
          <label>Número (sin +)
            <input type="text" value={form.whatsapp?.number || ''} onChange={(e) => updateWhatsapp('number', e.target.value)} />
          </label>
          <label>Mensaje
            <input type="text" value={form.whatsapp?.message || ''} onChange={(e) => updateWhatsapp('message', e.target.value)} />
          </label>
        </div>
        <label>Texto móvil
          <input type="text" value={form.whatsapp?.labelMobile || ''} onChange={(e) => updateWhatsapp('labelMobile', e.target.value)} />
        </label>
        <label>Texto desktop
          <input type="text" value={form.whatsapp?.labelDesktop || ''} onChange={(e) => updateWhatsapp('labelDesktop', e.target.value)} />
        </label>
      </section>
      )}

      {status && <p>{status}</p>}

      <button type="button" className="save-btn" onClick={handleSave} disabled={saving}>
        {saving ? 'Guardando...' : 'Guardar configuración BookMe'}
      </button>
    </div>
  );
};

export default BookMeLandingAdmin;
